<template>
  <div>
    <breadcrumb :items="breadcrumbs" />
    <v-container fluid>
      <div class="d-flex mb-8 align-center">
        <h2 class="font-weight-bold">{{ $t('carbonCredit.manageTrees.updateTreeInfo') }}</h2>
        <v-spacer></v-spacer>
        <v-btn outlined class="mr-3" style="border-color: #00bd73; color: #00bd73;" @click="cancel">
          {{ $t('carbonCredit.manageTrees.cancel') }}
        </v-btn>
        <v-btn color="primary" :loading="saving" @click="save">
          {{ $t('carbonCredit.manageTrees.save') }}
        </v-btn>
      </div>

      <v-form ref="form" lazy-validation>
        <v-card flat class="pa-6">
          <v-row v-if="fetchingTree">
            <v-col cols="12" class="text-center py-12">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </v-col>
          </v-row>

          <v-row v-else>
            <!-- Row 1 -->
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.idLabel') }}</label>
              <v-text-field v-model="form.tree_id_by_user" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.enterHere')" disabled class="disabled-field"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.additionalId') }}</label>
              <v-text-field v-model="form.additional_id" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.enterHere')"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.selectFarm') }}</label>
              <v-select v-model="form.farm_id" :items="farms" item-text="farmName" item-value="id" outlined dense
                :loading="fetchingFarms" :placeholder="$t('carbonCredit.manageTrees.makeASelection')" clearable
                :disabled="readonlyFields.farm_id" @change="onFarmChange"></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.selectZone') }}</label>
              <v-select v-model="form.zone_id" :items="zones" item-text="geofenceName" item-value="id" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.makeASelection')" clearable
                :disabled="!form.farm_id || readonlyFields.zone_id"></v-select>
            </v-col>

            <!-- Row 2 -->
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.gpsCoordinates') }}</label>
              <v-text-field v-model="form.gps_coordinates" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.enterHere')" :hint="$t('carbonCredit.manageTrees.gpsHint')"
                persistent-hint></v-text-field>
            </v-col>

            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.altitude') }}</label>
              <v-text-field v-model="form.altitude" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.enterHere')"></v-text-field>
            </v-col>

            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.locationInformation') }}</label>
              <v-text-field v-model="form.location_information" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.enterHere')"></v-text-field>
            </v-col>

            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.seedlingName') }}</label>
              <v-text-field v-model="form.seedling_name" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.enterHere')"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.selectTreeType') }}</label>
              <v-select v-model="form.tree_type_id" :items="dropdown.treeTypes" item-text="name" item-value="id"
                outlined dense :loading="fetchingDropdowns" :placeholder="$t('carbonCredit.manageTrees.makeASelection')"
                clearable :disabled="readonlyFields.tree_type_id" @change="form.tree_species_id = null"></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.selectTreeSpecies') }}</label>
              <v-select v-model="form.tree_species_id" :items="filteredTreeSpecies" item-text="name" item-value="id"
                outlined dense :placeholder="$t('carbonCredit.manageTrees.makeASelection')" clearable
                :disabled="!form.tree_type_id || readonlyFields.tree_species_id"></v-select>
            </v-col>

            <!-- Row 3 -->
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.datePlanted') }}</label>
              <v-menu v-model="datePicker" :close-on-content-click="false" transition="scale-transition" offset-y
                min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="form.date_planted" outlined dense readonly v-bind="attrs" v-on="on"
                    append-icon="mdi-calendar"></v-text-field>
                </template>
                <v-date-picker v-model="form.date_planted" :disabled="readonlyFields.date_planted"
                  @input="datePicker = false" no-title></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" md="3">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.selectHealthCondition') }}</label>
              <v-select v-model="form.health_condition" :items="dropdown.healthConditions" item-text="name"
                item-value="id" outlined dense :loading="fetchingDropdowns"
                :placeholder="$t('carbonCredit.manageTrees.makeASelection')" clearable
                :disabled="readonlyFields.health_condition"></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.selectModule') }}</label>
              <v-select v-model="form.program_type" :items="dropdown.programTypes" item-text="name" item-value="id"
                outlined dense :loading="fetchingDropdowns"
                :placeholder="$t('carbonCredit.manageTrees.makeASelection')" clearable></v-select>
            </v-col>
            <v-col cols="12" md="4" v-if="isRwtSelected">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.selectProject') }}</label>
              <v-select
                v-model="form.selected_nft_id"
                :items="nftOptions"
                item-text="name"
                item-value="id"
                outlined
                dense
                :loading="fetchingNfts"
                :placeholder="$t('carbonCredit.manageTrees.selectProject')"
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" md="4" v-if="isCarbonCreditSelected">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.selectProject') }}</label>
              <v-select
                v-model="form.selected_project_id"
                :items="carbonCreditProjectOptions"
                item-text="name"
                item-value="id"
                outlined
                dense
                :loading="fetchingCarbonCreditProjects"
                :placeholder="$t('carbonCredit.manageTrees.selectProject')"
                clearable
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <!-- Additional Notes -->
            <v-col cols="12" md="6">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.additionalNotes') }}</label>
              <v-textarea v-model="form.additional_note" outlined dense
                :placeholder="$t('carbonCredit.manageTrees.enterHere')" rows="5" no-resize></v-textarea>
            </v-col>
          </v-row>
          <v-row>
            <!-- Attach Images -->
            <v-col cols="12" md="6">
              <label class="field-label">{{ $t('carbonCredit.manageTrees.attachImages') }}</label>
              <div class="dropzone" @click="triggerFileInput" @dragover.prevent @drop.prevent="onFileDrop"
                @dragenter="dragging = true" @dragleave="dragging = false" :class="{ 'dropzone--active': dragging }">
                <v-icon size="40" color="#0057BD">mdi-tray-arrow-up</v-icon>
                <div class="mt-2">
                  <span class="blue--text text-decoration-underline" style="cursor: pointer;">
                    {{ $t('carbonCredit.manageTrees.selectFilesToUpload') }}
                  </span>
                </div>
                <div class="caption grey--text">{{ $t('carbonCredit.manageTrees.dragAndDrop') }}</div>
                <input ref="fileInput" type="file" multiple accept="image/*" style="display: none;"
                  @change="onFileChange" />
              </div>
              <div v-if="uploadedImages.length" class="d-flex flex-wrap mt-3">
                <div v-for="(img, idx) in uploadedImages" :key="idx" class="image-preview-item">
                  <v-img :src="img.url" width="80" height="80" cover class="rounded"></v-img>
                  <v-btn icon x-small class="remove-image-btn" @click="removeImage(idx)">
                    <v-icon small color="red">mdi-close-circle</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-form>
    </v-container>
  </div>
</template>

<script>
import CarbonCreditService from "@/_services/CarbonCreditService";
import { getAllFarmWithZones } from "@/_services/OptionService";
import S3UploadService from "@/_services/S3UploadService";
import loadingMixin from "@/mixins/LoadingMixin";

export default {
  name: "EditTree",
  mixins: [loadingMixin],

  data() {
    return {
      fullPage: true,
      saving: false,
      fetchingTree: false,
      fetchingFarms: false,
      fetchingDropdowns: false,
      fetchingNfts: false,
      fetchingCarbonCreditProjects: false,
      datePicker: false,
      dragging: false,
      uploadingImages: false,
      uploadedImages: [],
      form: {
        tree_id_by_user: "",
        additional_id: "",
        farm_id: null,
        zone_id: null,
        gps_coordinates: "",
        location_information: null,
        altitude: "",
        seedling_name: "",
        tree_type_id: null,
        tree_species_id: null,
        date_planted: new Date().toISOString().substr(0, 10),
        health_condition: null,
        program_type: null,
        selected_nft_id: null,
        selected_project_id: null,
        additional_note: "",
      },
      allFarmsWithZones: [],
      zones: [],
      nfts: [],
      carbonCreditProjects: [],
      dropdown: {
        treeTypes: [],
        treeSpecies: [],
        healthConditions: [],
        programTypes: [],
      },
      readonlyFields: {
        farm_id: true,
        zone_id: true,
        tree_type_id: true,
        tree_species_id: true,
        date_planted: true,
        health_condition: true,
      },
      breadcrumbs: [
        { text: "Dashboard", disabled: false, href: "/dashboard", exact: true },
        { text: "Manage Trees", disabled: false, href: "/manage-trees" },
        { text: "Edit Tree", disabled: true },
      ],
    };
  },

  computed: {
    treeId() {
      return this.$route.params.id;
    },
    farms() {
      return this.allFarmsWithZones.map(f => ({
        ...f,
        farmName: f.farmName || `#${f.id}`,
      }));
    },
    filteredTreeSpecies() {
      if (!this.form.tree_type_id) return this.dropdown.treeSpecies;
      return this.dropdown.treeSpecies.filter(
        s => String(s.tree_type_id) === String(this.form.tree_type_id)
      );
    },
    selectedProgramTypeName() {
      const selectedId = this.form.program_type;
      const selectedType = (this.dropdown.programTypes || []).find(
        (type) => String(type.id) === String(selectedId)
      );
      return String(selectedType?.name || "").toLowerCase();
    },
    isRwtSelected() {
      return this.selectedProgramTypeName.includes("rwa") || this.selectedProgramTypeName.includes("rwt");
    },
    isCarbonCreditSelected() {
      return this.selectedProgramTypeName.includes("carbon");
    },
    nftOptions() {
      return (this.nfts || [])
        .map((nft) => ({
          id: nft.id || nft._id || nft.nft_id || nft.tokenId,
          name: nft.name || nft.title || nft.tokenName || `NFT #${nft.id || nft.tokenId || ""}`,
        }))
        .filter((nft) => nft.id);
    },
    carbonCreditProjectOptions() {
      return (this.carbonCreditProjects || []).map((project) => ({
        id: project.id,
        name:
          project.project_title ||
          project.name ||
          `Project #${project.id}`,
      }));
    },
  },

  created() {
    this.fetchFarms();
    this.fetchDropdowns();
    this.fetchNfts();
    this.fetchCarbonCreditProjects();
    this.fetchTree();
  },

  watch: {
    async "form.program_type"() {
      if (this.isRwtSelected && !this.nfts.length) {
        await this.fetchNfts();
      }
      if (!this.isRwtSelected) {
        this.form.selected_nft_id = null;
      }

      if (this.isCarbonCreditSelected && !this.carbonCreditProjects.length) {
        await this.fetchCarbonCreditProjects();
      }
      if (!this.isCarbonCreditSelected) {
        this.form.selected_project_id = null;
      }
    },
  },

  methods: {
    async fetchTree() {
      this.fetchingTree = true;
      try {
        const res = await CarbonCreditService.getTreeById(this.treeId);
        const tree = res?.data?.data || res?.data || res;

        // Combine lat/lon back into GPS coordinates string for display
        const gps = (tree.lat && tree.lon)
          ? `${tree.lat}, ${tree.lon}`
          : (tree.gps_coordinates || "");

        this.form = {
          tree_id_by_user: tree.tree_id_by_user || "",
          additional_id: tree.additional_id || "",
          farm_id: tree.farm_id || null,
          zone_id: tree.zone_id || null,
          gps_coordinates: gps,
          location_information: tree.location_information || null,
          altitude: tree.altitude || "",
          seedling_name: tree.seedling_name || "",
          tree_type_id: tree.tree_type_id || null,
          tree_species_id: tree.tree_species_id || null,
          date_planted: tree.date_planted ? tree.date_planted.substr(0, 10) : new Date().toISOString().substr(0, 10),
          health_condition: tree.health_condition || null,
          program_type: Array.isArray(tree.program_type)
            ? (tree.program_type.map((p) => (typeof p === "object" ? p.id : p)).filter(Boolean)[0] || null)
            : null,
          selected_nft_id: tree.selected_nft_id != null ? tree.selected_nft_id : null,
          selected_project_id: tree.selected_project_id || null,
          additional_note: tree.additional_note || "",
        };

        // Pre-populate zones for the loaded farm
        if (tree.farm_id) {
          this._populateZones(tree.farm_id);
        }

        if (tree.my_tree_images?.length) {
          this.uploadedImages = tree.my_tree_images.map(img => ({
            url: img.file_url || img.url || img,
            name: img.s3_key || "image",
            s3_key: img.s3_key,
          }));
        }
      } catch (err) {
        console.error("Error fetching tree", err);
        this.$notify({ text: this.$t('carbonCredit.manageTrees.treeLoadFailed'), type: "error" });
      } finally {
        this.fetchingTree = false;
      }
    },

    async fetchFarms() {
      this.fetchingFarms = true;
      try {
        const farmList = await getAllFarmWithZones();
        this.allFarmsWithZones = farmList || [];
        // If farm is already set (tree loaded before farms), populate zones
        if (this.form.farm_id) {
          this._populateZones(this.form.farm_id);
        }
      } catch (err) {
        console.error("Error fetching farms", err);
      } finally {
        this.fetchingFarms = false;
      }
    },

    async fetchDropdowns() {
      this.fetchingDropdowns = true;
      try {
        const res = await CarbonCreditService.getTreeDropdownValues();
        const data = res.data || res;
        this.dropdown.treeTypes = data.treeTypes || [];
        this.dropdown.treeSpecies = data.treeSpecies || [];
        this.dropdown.healthConditions = data.healthConditions || [];
        this.dropdown.programTypes = data.programTypes || [];
      } catch (err) {
        console.error("Error fetching dropdowns", err);
      } finally {
        this.fetchingDropdowns = false;
      }
    },
    async fetchNfts() {
      this.fetchingNfts = true;
      try {
        const localRes = await CarbonCreditService.getNfts({ page: 1, limit: 100 });
        const payload = localRes?.data || localRes || {};
        const rows = payload?.rows || [];
        this.nfts = Array.isArray(rows) ? rows : [];
      } catch (err) {
        console.error("Error fetching NFTs", err);
      } finally {
        this.fetchingNfts = false;
      }
    },
    async fetchCarbonCreditProjects() {
      this.fetchingCarbonCreditProjects = true;
      try {
        const res = await CarbonCreditService.getCarbonCreditProjects({
          page: 1,
          limit: 1000,
        });
        const rows = res?.data?.rows || res?.rows || [];
        this.carbonCreditProjects = Array.isArray(rows) ? rows : [];
      } catch (err) {
        console.error("Error fetching carbon credit projects", err);
      } finally {
        this.fetchingCarbonCreditProjects = false;
      }
    },

    _populateZones(farmId) {
      const selectedFarm = this.allFarmsWithZones.find(
        f => String(f.id) === String(farmId)
      );
      const farmSegments = selectedFarm?.segments || selectedFarm?.Geofences || [];
      if (Array.isArray(farmSegments)) {
        const nonPrimarySegments = farmSegments.filter(s => !s.isPrimary);
        const segmentsToUse = nonPrimarySegments.length ? nonPrimarySegments : farmSegments;
        this.zones = segmentsToUse.map(s => ({
          ...s,
          geofenceName: s.geofenceName || (s.isPrimary ? "Primary Zone" : `Zone-${s.id}`),
        }));
      } else {
        this.zones = [];
      }
    },

    onFarmChange() {
      this.form.zone_id = null;
      this._populateZones(this.form.farm_id);
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    async onFileChange(event) {
      const files = Array.from(event.target.files);
      await this.processFiles(files);
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    async onFileDrop(event) {
      this.dragging = false;
      const files = Array.from(event.dataTransfer.files).filter(f => f.type.startsWith("image/"));
      await this.processFiles(files);
    },

    async processFiles(files) {
      const imageFiles = (files || []).filter((f) => f && f.type?.startsWith("image/"));
      if (!imageFiles.length) return;

      this.uploadingImages = true;
      this.startLoading();
      try {
        for (const file of imageFiles) {
          try {
            const result = await S3UploadService.imageUpload(file, "tree_images");
            this.uploadedImages.push({
              url: result.fullPath,
              name: file.name,
              s3_key: result.key,
            });
          } catch (uploadErr) {
            console.error("Error uploading tree image", uploadErr);
            this.$notify({
              text: uploadErr?.message || this.$t("carbonCredit.manageTrees.fileUploadFailed"),
              type: "error",
            });
          }
        }
      } finally {
        this.uploadingImages = false;
        this.stopLoading();
      }
    },

    removeImage(idx) {
      const img = this.uploadedImages[idx];
      if (img.file) URL.revokeObjectURL(img.url);
      this.uploadedImages.splice(idx, 1);
    },

    cancel() {
      this.$router.push({ name: "manageTrees" });
    },

    async save() {
      if (!this.$refs.form.validate()) return;
      if (this.uploadingImages) {
        this.$notify({ text: this.$t("loadingData"), type: "info" });
        return;
      }
      this.saving = true;
      try {
        // Parse GPS coordinates into lat/lon
        let lat = null;
        let lon = null;
        if (this.form.gps_coordinates) {
          const parts = this.form.gps_coordinates.split(",").map(p => p.trim());
          if (parts.length === 2) {
            lat = parseFloat(parts[0]) || null;
            lon = parseFloat(parts[1]) || null;
          }
        }

        const payload = {
          tree_id_by_user: this.form.tree_id_by_user,
          additional_id: this.form.additional_id,
          farm_id: this.form.farm_id,
          zone_id: this.form.zone_id,
          lat,
          lon,
          location_information: this.form.location_information,
          altitude: this.form.altitude?.trim() || null,
          seedling_name: this.form.seedling_name?.trim() || null,
          tree_type_id: this.form.tree_type_id,
          tree_species_id: this.form.tree_species_id,
          date_planted: this.form.date_planted,
          health_condition: this.form.health_condition,
          program_type: this.form.program_type ? [this.form.program_type] : [],
          selected_nft_id: this.form.selected_nft_id,
          selected_project_id: this.form.selected_project_id,
          additional_note: this.form.additional_note,
          images: this.uploadedImages
            .filter((img) => img?.s3_key)
            .map((img) => ({ s3_key: img.s3_key })),
        };

        await CarbonCreditService.updateTree(this.treeId, payload);
        this.$notify({ text: this.$t('carbonCredit.manageTrees.treeUpdatedSuccessfully'), type: "success" });
        this.$router.push({ name: "manageTrees" });
      } catch (err) {
        this.$notify({
          text: err?.response?.data?.message || this.$t('carbonCredit.manageTrees.treeUpdateFailed'),
          type: "error",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.field-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.dropzone {
  border: 2px dashed #bdbdbd;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;

  &:hover,
  &--active {
    border-color: #0057bd;
    background: #f0f6ff;
  }
}

.disabled-field {
  ::v-deep .v-input__slot {
    background-color: #f5f5f5 !important;
  }
}

.image-preview-item {
  position: relative;
  display: inline-block;
  margin-right: 8px;
  margin-bottom: 8px;
}

.remove-image-btn {
  position: absolute;
  top: -6px;
  right: -6px;
  background: white !important;
  border-radius: 50%;
}
</style>
